import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateRoomTypeRequestSchema,
  type CreateRoomTypeRequest,
  type RoomTypeDetail,
  type RoomTypeListItem,
  UpdateRoomTypeRequestSchema,
  type UpdateRoomTypeRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateRoomType, useUpdateRoomType } from '../use-room-types';

import { MasterCodeField } from '@/components/master-code-field';

interface RoomTypeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  roomType?: RoomTypeDetail | RoomTypeListItem | null;
}

const CREATE_DEFAULTS: CreateRoomTypeRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

const RoomTypeFields = ({
  pending,
  isCreate,
}: {
  pending: boolean;
  isCreate: boolean;
}): React.JSX.Element => (
  <>
    <MasterCodeField isCreate={isCreate} required />
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Deluxe Sea-View"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional details (e.g. King bed, balcony, ocean view)"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive room types are hidden from itinerary dropdowns."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const RoomTypeFormDrawer = ({
  open,
  onOpenChange,
  mode,
  roomType,
}: RoomTypeFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createRoomType = useCreateRoomType();
  const updateRoomType = useUpdateRoomType();
  const pending = createRoomType.isPending || updateRoomType.isPending;

  const createForm = useForm<CreateRoomTypeRequest>({
    resolver: zodResolver(CreateRoomTypeRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateRoomTypeRequest>({
    resolver: zodResolver(UpdateRoomTypeRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (roomType) {
      const description = (roomType as RoomTypeDetail).description ?? '';
      updateForm.reset({
        code: roomType.code,
        name: roomType.name,
        description,
        sortOrder: roomType.sortOrder,
        isActive: roomType.isActive,
      });
    }
  }, [open, isCreate, roomType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createRoomType.mutateAsync({
      ...values,
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!roomType) return;
    await updateRoomType.mutateAsync({
      id: roomType.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <RoomTypeFields pending={pending} isCreate={isCreate} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add room type' : `Edit ${roomType?.name ?? 'room type'}`}
      description="The code is the stable machine identifier (e.g. STD, DLX). The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
